import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { getMe, getVip } from "../../apis/auth";
import { queryContants } from "../../constant";

// Helper to clear invalid token
const clearInvalidToken = (queryClient) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    console.log("Clearing invalid token...");
    localStorage.removeItem("accessToken");
    // Clear all queries that depend on auth to prevent refetch
    queryClient.cancelQueries({ queryKey: [queryContants.GET_ME] });
    queryClient.cancelQueries({ queryKey: [queryContants.GET_VIP] });
    queryClient.removeQueries({ queryKey: [queryContants.GET_ME] });
    queryClient.removeQueries({ queryKey: [queryContants.GET_VIP] });
  }
};

export const useGetMeQuery = () => {
  const queryClient = useQueryClient();
  const accessToken = localStorage.getItem("accessToken");
  const hasErrorRef = useRef(false);
  
  const query = useQuery({
    queryKey: [queryContants.GET_ME],
    queryFn: getMe,
    enabled: !!accessToken && !hasErrorRef.current,
    refetchInterval: (!!accessToken && !hasErrorRef.current) ? 30000 : false,
    refetchOnWindowFocus: !!accessToken && !hasErrorRef.current,
    refetchOnMount: !!accessToken && !hasErrorRef.current,
    retry: false, // Don't retry on 401/404 errors
    retryOnMount: false, // Don't retry on mount if previous attempt failed
    onError: (error) => {
      // Clear token if 401 or 404 (invalid token)
      const status = error?.response?.status;
      if (status === 401 || status === 404) {
        hasErrorRef.current = true;
        clearInvalidToken(queryClient);
      }
    },
    onSuccess: () => {
      // Reset error flag on success
      hasErrorRef.current = false;
    },
  });

  // Watch for token removal and disable query
  useEffect(() => {
    if (!accessToken && hasErrorRef.current) {
      hasErrorRef.current = false;
      queryClient.cancelQueries({ queryKey: [queryContants.GET_ME] });
    }
  }, [accessToken, queryClient]);

  return query;
};

export const useGetVipQuery = () => {
  const queryClient = useQueryClient();
  const accessToken = localStorage.getItem("accessToken");
  const hasErrorRef = useRef(false);
  
  const query = useQuery({
    queryKey: [queryContants.GET_VIP],
    queryFn: getVip,
    enabled: !!accessToken && !hasErrorRef.current,
    refetchOnWindowFocus: !!accessToken && !hasErrorRef.current,
    refetchOnMount: !!accessToken && !hasErrorRef.current,
    retry: false, // Don't retry on 401/404 errors
    retryOnMount: false, // Don't retry on mount if previous attempt failed
    onError: (error) => {
      // Clear token if 401 or 404 (invalid token)
      const status = error?.response?.status;
      if (status === 401 || status === 404) {
        hasErrorRef.current = true;
        clearInvalidToken(queryClient);
      }
    },
    onSuccess: () => {
      // Reset error flag on success
      hasErrorRef.current = false;
    },
  });

  // Watch for token removal and disable query
  useEffect(() => {
    if (!accessToken && hasErrorRef.current) {
      hasErrorRef.current = false;
      queryClient.cancelQueries({ queryKey: [queryContants.GET_VIP] });
    }
  }, [accessToken, queryClient]);

  return query;
};
